
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Clock, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTicketReplies } from '@/hooks/useTickets';
import { useAuthSession } from '@/hooks/useAuthSession';
import type { Ticket } from '@/types/ticket';

interface TicketDetailsDialogProps {
  ticket: Ticket | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const priorityColors = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800'
};

const statusColors = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800'
};

export function TicketDetailsDialog({ ticket, open, onOpenChange }: TicketDetailsDialogProps) {
  const [replyMessage, setReplyMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { session } = useAuthSession();
  const { replies, isLoading, createReply } = useTicketReplies(ticket?.id || '');

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyMessage.trim() || !ticket) return;

    setIsSubmitting(true);
    const success = await createReply({
      ticket_id: ticket.id,
      message: replyMessage.trim()
    });

    if (success) {
      setReplyMessage('');
    }
    setIsSubmitting(false);
  };

  if (!ticket) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="truncate">{ticket.title}</span>
            <div className="flex gap-2 ml-4">
              <Badge className={priorityColors[ticket.priority]}>
                {ticket.priority}
              </Badge>
              <Badge className={statusColors[ticket.status]}>
                {ticket.status.replace('_', ' ')}
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="space-y-4 mb-4">
            <div>
              <h4 className="font-medium mb-2">Description</h4>
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                {ticket.description}
              </p>
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>
                  Created {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>Category: {ticket.category}</span>
              </div>
            </div>
          </div>

          <Separator />

          <div className="flex-1 flex flex-col min-h-0">
            <h4 className="font-medium my-3">Conversation</h4>
            
            <ScrollArea className="flex-1 pr-4">
              {isLoading ? (
                <div className="text-center text-muted-foreground py-8">
                  Loading replies...
                </div>
              ) : replies.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No replies yet. Start the conversation!
                </div>
              ) : (
                <div className="space-y-4">
                  {replies.map((reply) => (
                    <div
                      key={reply.id}
                      className={`p-3 rounded-md ${
                        reply.user_id === session?.user?.id
                          ? 'bg-blue-50 ml-8'
                          : 'bg-muted mr-8'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium">
                          {reply.user_id === session?.user?.id ? 'You' : 'Support'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm">{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {ticket.status !== 'closed' && (
              <form onSubmit={handleSubmitReply} className="mt-4 space-y-3">
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={isSubmitting || !replyMessage.trim()} size="sm">
                    <Send className="h-4 w-4 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
